<template>
  <div>
    <v-simple-table
      class="table-sm"
      v-if="show_table"
      style="transition: 0.3s ease-in-out"
    >
      <tbody>
        <tr v-if="!editing">
          <td colspan="3">
            <v-alert
              v-if="!settings"
              dense
              type="info"
              color="blue lighten-5 blue--text"
            >
              <li>
                If you want to write the value after printing this invoice,
                leave the value input empty.
              </li>
              <li>Rows without labels would not be saved.</li>
            </v-alert>
          </td>
        </tr>
        <tr v-for="(item, index) in items" :key="index">
          <td class="disabled">
            <v-text-field
              :placeholder="labels.label"
              v-model="item.label"
              dense
              @blur="$emit('itemsChanged', { items, show_table })"
            ></v-text-field>
          </td>
          <td>
            <v-text-field
              :placeholder="labels.value"
              v-model="item.value"
              dense
              clearable
              @blur="$emit('itemsChanged', { items, show_table })"
              @click:append="$emit('itemsChanged', { items, show_table })"
            ></v-text-field>
          </td>

          <td>
            <v-btn icon color="red" @click="remove(index)">
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
          </td>
        </tr>
        <tr>
          <td colspan="3">
            <v-btn
              @click="add"
              x-small
              :block="!editing || hideDone"
              text
              :color="hasBrandColors ? undefined : 'blue'"
              :class="{ 'branded-back-btn': hasBrandColors }"
              :style="hasBrandColors ? backButtonStyle : {}"
              rounded
              :id="labels.addBtnId"
            >
              <v-icon>mdi-plus</v-icon>
              {{ labels.rowBtn }}
            </v-btn>
            <v-btn
              v-if="editing && !hideDone"
              @click="$emit('done', { items, show_table })"
              color="green"
              depressed
              dark
              rounded
              >Done
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-simple-table>
    <v-btn
      :color="show_table ? 'red' : hasBrandColors ? undefined : 'blue'"
      small
      text
      v-if="!editing && !settings"
      block
      rounded
      :class="{ 'branded-back-btn': hasBrandColors && !show_table }"
      :style="hasBrandColors && !show_table ? backButtonStyle : {}"
      :id="labels.addBtnId"
      @click="
        show_table = !show_table;
        $emit('itemsChanged', { items, show_table });
      "
    >
      {{ show_table ? labels.removeBtn : labels.addBtn }}
    </v-btn>
    <v-btn
      block
      rounded
      depressed
      dark
      :color="
        $store.state.user.user_infor.current_business.invoice_settings
          .invoice_color
      "
      class="mt-3"
      v-if="settings"
      @click="saveItems"
      :loading="savingItems"
      >{{ labels.saveBtn }}
    </v-btn>

    <v-snackbar v-model="show_success_message"
      >{{ success_message }}
    </v-snackbar>
    <v-snackbar v-model="show_error_message">{{ error_message }} </v-snackbar>
  </div>
</template>

<script>
import eventBus from "@/utils";
import {
  getBrandPrimaryButtonStyle,
  getBrandSecondaryStyleVars,
  hasPartnerBrandColors,
} from "@/utils/brandColors";

export default {
  name: "extraHeadersComponent",
  emits: ["itemsSaved", "itemsChanged", "done"],
  props: {
    defaultItems: { type: Array, default: () => [] },
    showItems: { type: Boolean, default: false },
    editing: { type: Boolean, default: false },
    hideDone: { type: Boolean, default: false },
    settings: { type: Boolean, default: false },
    type: { type: String, default: "header" },
  },
  data() {
    return {
      items: [],
      show_table: false,
      savingItems: false,
      success_message: "",
      error_message: "",
      show_success_message: false,
      show_error_message: false,
    };
  },
  computed: {
    business() {
      return this.$store.state.user.user_infor.current_business;
    },
    hasBrandColors() {
      return hasPartnerBrandColors(this.business);
    },
    backButtonStyle() {
      return this.hasBrandColors ? getBrandSecondaryStyleVars(this.business) : {};
    },
    saveButtonStyle() {
      return this.hasBrandColors
        ? getBrandPrimaryButtonStyle(this.business)
        : {};
    },
    eventBus() {
      return eventBus;
    },
    apiEndpoint() {
      return this.type === "footer"
        ? "/api/invoice-footers"
        : "/api/invoice/headers";
    },
    labels() {
      if (this.type === "footer") {
        return {
          label: "Label",
          value: "Value",
          rowBtn: "Row",
          addBtn: "Add extra information",
          removeBtn: "Remove extra information",
          saveBtn: "Save Information",
          addBtnId: "InvoiceExtraInformation",
        };
      } else {
        return {
          label: "Header label",
          value: "Header value",
          rowBtn: "Row",
          addBtn: "Add extra header Info.",
          removeBtn: "Remove extra header Info.",
          saveBtn: "Save Headers",
          addBtnId: "InvoiceExtraHeaders",
        };
      }
    },
  },
  watch: {
    defaultItems() {
      this.items = this.defaultItems;
    },
  },
  methods: {
    remove(index) {
      this.items.splice(index, 1);
      this.$emit("itemsChanged", {
        items: this.items,
        show_table: this.show_table,
      });
    },
    add() {
      this.items.push({ label: "", value: "" });
      this.$emit("itemsChanged", {
        items: this.items,
        show_table: this.show_table,
      });
    },
    getItems() {
      this.$axios
        ? this.$axios.get(this.apiEndpoint)
        : axios.get(this.apiEndpoint).then((res) => {
            this.items = res.data;
          });
    },
    saveItems() {
      this.savingItems = true;
      const data =
        this.type === "footer"
          ? { invoiceFooters: this.items }
          : { invoiceHeaders: this.items };
      (this.$axios ? this.$axios : axios)
        .post(this.apiEndpoint, data)
        .then((res) => {
          this.success_message =
            this.type === "footer"
              ? "Invoices extra information saved"
              : "Invoices headers saved";
          this.show_success_message = true;
          this.savingItems = false;
          this.$emit("itemsSaved", res.data);
        });
    },
  },
  mounted() {
    this.show_table = !!this.settings;
    if (this.editing) {
      this.items = this.defaultItems;
      this.show_table = this.showItems;
    } else {
      this.getItems();
    }
  },
};
</script>

<style scoped>
.branded-back-btn:hover,
.branded-back-btn:hover .v-btn__content,
.branded-back-btn:hover .v-icon {
  color: var(--brand-secondary) !important;
}
</style>
